import fetcher, { fetcherEnhanced, RequestOptions } from '@cdc-internal/fe-toolbox/libs/fetcher'
import { isClientEnv, isServerEnv } from '@cdc-internal/fe-toolbox/utils/env'

import type { FetcherCustomData } from '@/types/api'

import { defaultNextjsFetchCacheOptions, timeout } from './configs'
import { handleSilent404 } from './hooks'
import { handleClientFetchError, logFetchError } from './utils'

// fetcher docs: https://github.com/cdc-internal/fe-toolbox/wiki/libs-fetcher

export type FetcherFunctionType = typeof fetcher
export type FetcherEnhancedFunctionType = typeof fetcherEnhanced

const defaultRequestOptions: RequestOptions = {
  headers: {
    // Custom X-Client header is used to identify the api caller in BE server
    'X-Client': isServerEnv ? 'NewPriceNodeServer' : 'NewPriceWebPages',
  },
}

export const defaultFetcher: FetcherFunctionType = (url, requestOptions, extendedOptions) =>
  fetcher(
    url,
    {
      ...requestOptions,
      headers: {
        ...(extendedOptions?.customData?.addCustomRequestHeaders === false
          ? {}
          : defaultRequestOptions.headers),
        ...requestOptions?.headers,
      },
      ...(extendedOptions?.customData?.enableDefaultNextjsServerFetchCache
        ? defaultNextjsFetchCacheOptions
        : {}),
    },
    {
      timeout,
      ...extendedOptions,
      afterResponseHooks: [...(extendedOptions?.afterResponseHooks || []), handleSilent404],
      onFetchError: error => {
        // Skip error handling for 404 if silent404 is enabled
        if (extendedOptions?.customData?.silent404 && error?.statusCode === 404) {
          return
        }

        return isClientEnv
          ? handleClientFetchError(error, url, {
              ...extendedOptions,
              customData: {
                enableServerErrorNotification:
                  requestOptions?.method === undefined || requestOptions?.method === 'GET',
                ...extendedOptions?.customData,
              } as FetcherCustomData,
            })
          : logFetchError(error, url)
      },
    },
  )

export const defaultFetcherEnhanced: FetcherEnhancedFunctionType = (
  url,
  requestOptions,
  extendedOptions,
) => fetcherEnhanced(url, requestOptions, extendedOptions)
